import { clienteDisplay } from '@/lib/types';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DollarSign, Briefcase, TrendingUp, Users, Plus, X, Mail, Phone, Hash, Eye, EyeOff,
  CheckCircle2, Clock, KeyRound, Copy, Loader2, ChevronDown, ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';
import { PageHeader } from '@/components/PageHeader';
import { KpiCard } from '@/components/KpiCard';
import { KpiCardSkeleton } from '@/components/Skeleton';
import { CadastrarContratoModal } from '@/components/CadastrarContratoModal';
import NotasPanel from '@/components/NotasPanel';
import { DistribuicoesPendentesWidget } from '@/components/DistribuicoesPendentesWidget';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Contrato, Solicitante, Cota, Demanda, Pessoa, Area, CotaFamilia, StatusDemanda, EntregaEscopo, FaseEscopo, StatusEntrega } from '@/lib/types';
import { FASES_ESCOPO } from '@/lib/types';

type GrupoKey = CotaFamilia | 'Apex / Research';
const GRUPOS_ORDEM: GrupoKey[] = ['Conecta Folha Business', 'BRM - BUY', 'Setorial', 'Apex / Research'];
const GRUPO_COR: Record<GrupoKey, string> = {
  'Conecta Folha Business': 'border-blue/40',
  'BRM - BUY':              'border-purple/40',
  'Setorial':               'border-pink/40',
  'Pesquisa':               'border-green/40',
  'Apex / Research':        'border-green/40',
};

function valoresVisiveis(): boolean {
  // Se o user não pode ver valores → SEMPRE oculto, ignora localStorage.
  try {
    const can = (window as unknown as { __apexCanVerValores?: boolean }).__apexCanVerValores;
    if (can === false) return false;
    return localStorage.getItem('apex_hide_valores') !== '1';
  } catch { return false; }
}

function fmtBRL(n: number): string {
  if (!valoresVisiveis()) return 'R$ ••••';
  return fmtBRLraw(n);
}

function fmtBRLraw(n: number): string {
  if (n >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1).replace('.0', '')}M`;
  if (n >= 1_000) return `R$ ${(n / 1_000).toFixed(0)}k`;
  return `R$ ${n.toLocaleString('pt-BR')}`;
}

export function Comercial() {
  const { user } = useAuth();
  const [hideValores, setHideValores] = useState(() => {
    try { return localStorage.getItem('apex_hide_valores') === '1'; } catch { return false; }
  });
  const podeVerValores = user?.canVerValores === true;
  function toggleHide() {
    setHideValores(v => {
      const n = !v;
      try { localStorage.setItem('apex_hide_valores', n ? '1' : '0'); } catch {}
      return n;
    });
  }
  const [searchParams, setSearchParams] = useSearchParams();
  const [clienteOpen, setClienteOpen] = useState<string | null>(null);
  const [novoOpen, setNovoOpen] = useState(false);

  // Auto-abrir modal quando sidebar clica "Novo contrato" (?nova=1)
  useEffect(() => {
    if (searchParams.get('nova') === '1') {
      setNovoOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [drawerKpi, setDrawerKpi] = useState<null | 'renovacao' | 'mrr'>(null);

  const contratosQ = useQuery({ queryKey: ['contratos'], queryFn: () => api.contratos.list().then((r) => r.data) });
  const cotasQ = useQuery({ queryKey: ['cotas'], queryFn: () => api.cotas.list().then((r) => r.data) });
  const solicitantesQ = useQuery({ queryKey: ['solicitantes'], queryFn: () => api.solicitantes.list().then((r) => r.data) });
  const demandasQ = useQuery({ queryKey: ['demandas'], queryFn: () => api.demandas.list().then((r) => r.data) });
  const pessoasQ = useQuery({ queryKey: ['pessoas'], queryFn: () => api.pessoas.list().then((r) => r.data) });
  const areasQ = useQuery({ queryKey: ['areas'], queryFn: () => api.areas.list().then((r) => r.data) });

  const contratos = contratosQ.data ?? [];
  const cotas = cotasQ.data ?? [];
  const solicitantes = solicitantesQ.data ?? [];
  const demandas = demandasQ.data ?? [];
  const pessoas = pessoasQ.data ?? [];
  const areas = areasQ.data ?? [];

  const cotaById = useMemo(() => new Map(cotas.map((c) => [c.id, c])), [cotas]);
  const solById = useMemo(() => new Map(solicitantes.map((s) => [s.id, s])), [solicitantes]);

  const vigentes = contratos.filter((c) => c.status === 'Vigente');
  const renovacao = contratos.filter((c) => c.status === 'Renovação em curso');
  const mrr = vigentes.reduce((s, c) => s + (c.valorMensal || 0), 0);
  const ticketMedio = vigentes.length ? mrr / vigentes.length : 0;

  // Filtra fora os Apex Interno e "Apex Partners (institucional)" — não entram no Comercial
  const solicitantesComerciais = useMemo(
    () => solicitantes.filter((s) =>
      s.categoria !== 'Apex Interno' &&
      !/apex\s*partners/i.test(s.nome),
    ),
    [solicitantes],
  );

  const grupoPorSolicitante = useMemo(() => {
    const m = new Map<string, GrupoKey>();
    for (const c of contratos) {
      if (c.status !== 'Vigente' && c.status !== 'Renovação em curso') continue;
      const fam = c.cotaId ? cotaById.get(c.cotaId)?.familia : undefined;
      if (fam) m.set(c.solicitanteId, fam as GrupoKey);
    }
    for (const s of solicitantesComerciais) {
      if (m.has(s.id)) continue;
      if (s.categoria === 'Research Comercial') m.set(s.id, 'Apex / Research');
    }
    return m;
  }, [contratos, cotaById, solicitantesComerciais]);

  const clientesPorGrupo = useMemo(() => {
    const m: Record<GrupoKey, { solicitante: Solicitante; contrato?: Contrato; cota?: Cota }[]> = {
      'Conecta Folha Business': [], 'BRM - BUY': [], 'Setorial': [], 'Pesquisa': [], 'Apex / Research': [],
    };
    const seen = new Set<string>();
    for (const c of [...vigentes, ...renovacao]) {
      if (seen.has(c.solicitanteId)) continue;
      const sol = solById.get(c.solicitanteId);
      const grupo = grupoPorSolicitante.get(c.solicitanteId);
      if (!sol || !grupo) continue;
      seen.add(c.solicitanteId);
      m[grupo].push({ solicitante: sol, contrato: c, cota: c.cotaId ? cotaById.get(c.cotaId) : undefined });
    }
    for (const s of solicitantesComerciais) {
      if (seen.has(s.id)) continue;
      const grupo = grupoPorSolicitante.get(s.id);
      if (grupo === 'Apex / Research') {
        seen.add(s.id);
        m['Apex / Research'].push({ solicitante: s });
      }
    }
    for (const k of GRUPOS_ORDEM) {
      m[k].sort((a, b) => a.solicitante.nome.localeCompare(b.solicitante.nome));
    }
    return m;
  }, [vigentes, renovacao, solById, cotaById, grupoPorSolicitante, solicitantesComerciais]);

  /* ── Clientes inativos (D-12) ── */
  const [inativosOpen, setInativosOpen] = useState(false);
  const clientesInativos = useMemo(() => {
    return solicitantesComerciais
      .filter((s) => s.status === 'Inativo')
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [solicitantesComerciais]);

  const clienteAberto = useMemo(() => {
    if (!clienteOpen) return null;
    // Procura nos grupos ativos
    for (const k of GRUPOS_ORDEM) {
      const found = clientesPorGrupo[k].find((c) => c.solicitante.id === clienteOpen);
      if (found) return found;
    }
    // Procura nos inativos
    const inativo = clientesInativos.find((s) => s.id === clienteOpen);
    if (inativo) {
      const cont = contratos.find((c) => c.solicitanteId === inativo.id);
      return { solicitante: inativo, contrato: cont, cota: cont?.cotaId ? cotaById.get(cont.cotaId) : undefined };
    }
    return null;
  }, [clienteOpen, clientesPorGrupo, clientesInativos, contratos, cotaById]);

  const loading = contratosQ.isLoading || cotasQ.isLoading || solicitantesQ.isLoading;

  return (
    <div>
      <PageHeader
        title="Plataforma"
        action={
          podeVerValores && (
            <button type="button" onClick={toggleHide}
              className={`btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5 ${hideValores ? 'bg-orange/15 border-orange/40 text-orange' : ''}`}
              title={hideValores ? 'Mostrar valores' : 'Ocultar valores (modo apresentação)'}>
              {hideValores ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {hideValores ? 'Mostrar cifras' : 'Modo apresentação'}
            </button>
          )
        }
      />

      <div className="px-6 py-4 space-y-4">
        <DistribuicoesPendentesWidget />
        {loading ? (
          <div className="grid grid-cols-4 gap-3">{[1,2,3,4].map((i) => <KpiCardSkeleton key={i} />)}</div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-3">
              <button type="button" onClick={() => setDrawerKpi('mrr')}
                className="text-left hover:ring-1 hover:ring-blue/40 rounded-xl transition-shadow">
                <KpiCard label="MRR atual" color="blue" value={fmtBRL(mrr)}
                  delta={`${vigentes.length} vigentes · ver lista`} icon={<DollarSign className="w-4 h-4" />} />
              </button>
              <KpiCard label="Ticket médio" color="green" value={fmtBRL(ticketMedio)}
                delta="por contrato vigente" icon={<TrendingUp className="w-4 h-4" />} />
              <button type="button" onClick={() => renovacao.length && setDrawerKpi('renovacao')}
                disabled={!renovacao.length}
                className={clsx(
                  'text-left rounded-xl transition-shadow',
                  renovacao.length ? 'hover:ring-1 hover:ring-orange/40' : 'cursor-default opacity-90',
                )}>
                <KpiCard label="Em renovação" color="orange" value={renovacao.length}
                  delta={renovacao.length ? 'clique pra ver quais' : 'nenhum em renovação'}
                  icon={<Briefcase className="w-4 h-4" />} />
              </button>
              <KpiCard label="Clientes ativos" color="purple" value={vigentes.length}
                delta="contas com contrato em vigência" icon={<Users className="w-4 h-4" />} />
            </div>

            {/* Cards de clientes */}
            <div>
              <h2 className="text-sm font-bold mb-3">Carteira de clientes</h2>
              <div className="space-y-4">
                {GRUPOS_ORDEM.map((grupo) => {
                  const lista = clientesPorGrupo[grupo];
                  if (!lista.length) return null;
                  return (
                    <section key={grupo}>
                      <h2 className="text-xs font-bold uppercase tracking-wider text-text-2 mb-2">
                        {grupo} <span className="text-muted font-normal">· {lista.length}</span>
                      </h2>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                        {lista.map(({ solicitante, contrato, cota }) => (
                          <button
                            key={solicitante.id}
                            type="button"
                            onClick={() => setClienteOpen(solicitante.id)}
                            className={clsx(
                              'bg-panel border border-border border-2 rounded-md p-3 text-left hover:border-blue/50 hover:bg-panel-2 transition-colors',
                              GRUPO_COR[grupo],
                            )}
                          >
                            <div className="text-xs font-bold leading-tight mb-1 line-clamp-2">{solicitante.nome}</div>
                            {cota && <div className="text-[10px] text-text-2 mb-1">{cota.hierarquia}</div>}
                            <div className="text-[10px] uppercase tracking-wider text-muted">
                              {contrato?.status === 'Vigente' ? 'vigente' : contrato?.status === 'Renovação em curso' ? 'em renovação' : 'interno'}
                            </div>
                          </button>
                        ))}
                      </div>
                    </section>
                  );
                })}

                {/* D-12: Clientes Inativos (collapsible) */}
                {clientesInativos.length > 0 && (
                  <section className="border-t border-border pt-4 mt-2">
                    <button
                      type="button"
                      onClick={() => setInativosOpen((v) => !v)}
                      className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted hover:text-text transition-colors mb-2"
                    >
                      {inativosOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      Clientes inativos <span className="font-normal">· {clientesInativos.length}</span>
                    </button>
                    {inativosOpen && (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                        {clientesInativos.map((sol) => (
                          <button
                            key={sol.id}
                            type="button"
                            onClick={() => setClienteOpen(sol.id)}
                            className="bg-panel border border-border border-2 border-dashed rounded-md p-3 text-left hover:border-muted hover:bg-panel-2 transition-colors opacity-60 hover:opacity-100"
                          >
                            <div className="text-xs font-bold leading-tight mb-1 line-clamp-2">{sol.nome}</div>
                            <div className="text-[10px] uppercase tracking-wider text-red">encerrado</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </section>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {clienteAberto && (
        <ClienteDetailsModal
          cliente={clienteAberto.solicitante}
          contrato={clienteAberto.contrato}
          cota={clienteAberto.cota}
          demandas={demandas.filter((d) => d.solicitanteId === clienteAberto.solicitante.id)}
          pessoas={pessoas}
          areas={areas}
          onClose={() => setClienteOpen(null)}
        />
      )}

      {novoOpen && <CadastrarContratoModal solicitantes={solicitantes} cotas={cotas} onClose={() => setNovoOpen(false)} />}

      {drawerKpi && (
        <ContratosKpiDrawer
          kind={drawerKpi}
          contratos={drawerKpi === 'mrr' ? vigentes : renovacao}
          solById={solById}
          cotaById={cotaById}
          onClose={() => setDrawerKpi(null)}
          onClickCliente={(sid) => { setDrawerKpi(null); setClienteOpen(sid); }}
        />
      )}
    </div>
  );
}

function ContratosKpiDrawer({ kind, contratos, solById, cotaById, onClose, onClickCliente }: {
  kind: 'mrr' | 'renovacao';
  contratos: Contrato[];
  solById: Map<string, Solicitante>;
  cotaById: Map<string, Cota>;
  onClose: () => void;
  onClickCliente: (id: string) => void;
}) {
  const titulo = kind === 'mrr' ? 'Contratos vigentes · MRR' : 'Contratos em renovação';
  const sub = kind === 'mrr'
    ? `${contratos.length} contratos · ${fmtBRL(contratos.reduce((s, c) => s + (c.valorMensal || 0), 0))} de receita mensal`
    : `${contratos.length} cliente${contratos.length === 1 ? '' : 's'} no funil de renovação`;
  const total = contratos.reduce((s, c) => s + (c.valorMensal || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-panel border-l border-border w-full max-w-md h-full overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border sticky top-0 bg-panel z-10">
          <div>
            <h2 className="text-sm font-bold">{titulo}</h2>
            <p className="text-[11px] text-muted mt-0.5">{sub}</p>
          </div>
          <button type="button" onClick={onClose} className="text-muted hover:text-text"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-1.5">
          {contratos.length === 0 && <div className="text-xs text-muted">Nenhum contrato.</div>}
          {contratos.map((c) => {
            const sol = solById.get(c.solicitanteId);
            const cota = c.cotaId ? cotaById.get(c.cotaId) : undefined;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => sol && onClickCliente(sol.id)}
                className="w-full bg-panel-2 border border-border rounded p-2.5 text-left hover:border-blue/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-semibold text-text text-xs leading-snug truncate flex-1">{clienteDisplay(sol?.nome)}</div>
                  {kind === 'mrr' && (
                    <div className="text-xs font-bold text-blue-light shrink-0">{fmtBRL(c.valorMensal || 0)}</div>
                  )}
                </div>
                <div className="text-[10px] text-muted">
                  {c.identificador && <span>{c.identificador}</span>}
                  {cota && <span> · {cota.nome}</span>}
                </div>
                {(c.vigenciaInicio || c.vigenciaFim) && (
                  <div className="text-[10px] text-text-2 mt-1">
                    {c.vigenciaInicio ? new Date(c.vigenciaInicio).toLocaleDateString('pt-BR') : '—'}
                    {' → '}
                    {c.vigenciaFim ? new Date(c.vigenciaFim).toLocaleDateString('pt-BR') : '—'}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        {kind === 'mrr' && contratos.length > 0 && (
          <div className="px-4 py-3 border-t border-border bg-panel-2 sticky bottom-0">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted">Total mensal</span>
              <span className="font-bold text-blue-light">{fmtBRL(total)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_PILL: Record<StatusDemanda, { label: string; cls: string }> = {
  'Triagem':              { label: 'Briefing',            cls: 'bg-blue/15 text-blue-light' },
  'Em produção':          { label: 'Em produção',         cls: 'bg-orange/15 text-orange' },
  'Em revisão':           { label: 'Em revisão',          cls: 'bg-purple/15 text-purple' },
  'Aguardando aprovação': { label: 'Aguardando aprov.',   cls: 'bg-pink/15 text-pink' },
  'Publicada':            { label: 'Finalizado',          cls: 'bg-green/15 text-green' },
  'Arquivada':            { label: 'Arquivada',           cls: 'bg-muted/15 text-muted' },
  'Cancelada':            { label: 'Cancelada',           cls: 'bg-red/15 text-red' },
};

function ClienteDetailsModal({
  cliente, contrato, cota, demandas, pessoas, areas, onClose,
}: { cliente: Solicitante; contrato?: Contrato; cota?: Cota; demandas: Demanda[]; pessoas: Pessoa[]; areas: Area[]; onClose: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'visao' | 'escopo' | 'entregas' | 'responsaveis' | 'notas'>('visao');
  const ativas = demandas.filter((d) => ['Triagem', 'Em produção', 'Em revisão', 'Aguardando aprovação'].includes(d.status));

  // Escopo de entregas do contrato
  const escopoQ = useQuery({
    queryKey: ['escopo-entregas', contrato?.id],
    queryFn: () => contrato ? api.escopoEntregas.list(contrato.id).then((r) => r.data) : Promise.resolve([]),
    enabled: !!contrato,
    staleTime: 2 * 60_000,
  });
  const escopoEntregas = escopoQ.data ?? [];
  const publicadas = demandas.filter((d) => d.status === 'Publicada');
  const aguardando = demandas.filter((d) => d.status === 'Aguardando aprovação');

  const pessoaById = useMemo(() => new Map(pessoas.map((p) => [p.id, p])), [pessoas]);
  const areaById = useMemo(() => new Map(areas.map((a) => [a.id, a])), [areas]);

  const WHITELIST_ENCERRAR = ['mariana@apexpartners.com.br', 'mariana.dicavalcanti@apexpartners.com.br'];
  const podeEncerrar = user?.papel === 'Coordenação' || user?.papel === 'Diretoria' || (user?.email && WHITELIST_ENCERRAR.includes(user.email.toLowerCase()));

  const encerrar = useMutation({
    mutationFn: (contratoId: string) => api.contratos.encerrar(contratoId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contratos'] });
      qc.invalidateQueries({ queryKey: ['solicitantes'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-panel border border-border rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-panel sticky top-0 z-10">
          <div>
            <h2 className="text-sm font-bold">{cliente.nome}</h2>
            <p className="text-[10px] text-muted mt-0.5">{cliente.setor ?? '—'}</p>
          </div>
          <button type="button" onClick={onClose} className="text-muted hover:text-text"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex items-center gap-1 px-5 border-b border-border bg-panel-2">
          {(['visao','escopo','entregas','responsaveis','notas'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={clsx(
                'px-3 py-2 text-[11px] font-semibold border-b-2 -mb-px transition-colors',
                tab === t ? 'text-blue-light border-blue' : 'text-text-2 border-transparent hover:text-text',
              )}
            >
              {t === 'visao' ? 'Visão' : t === 'escopo' ? `Escopo (${escopoEntregas.length})` : t === 'entregas' ? `Entregas (${demandas.length})` : t === 'responsaveis' ? 'Responsáveis' : 'Notas'}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
          {tab === 'visao' && (
            <>
              <div className="grid grid-cols-4 gap-2">
                <MiniKpi label="Em produção" value={ativas.length - aguardando.length} color="orange" />
                <MiniKpi label="Aguardando" value={aguardando.length} color="pink" />
                <MiniKpi label="Finalizadas" value={publicadas.length} color="green" />
                <MiniKpi label="Total" value={demandas.length} color="blue" />
              </div>

              <section>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    window.location.href = '/demandas?nova=1&solicitante=' + cliente.id;
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold text-blue-light bg-blue/10 border border-blue/30 hover:bg-blue/15 rounded-lg transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Adicionar demanda fora do escopo
                </button>
                <p className="text-[10px] text-muted mt-1.5 text-center">
                  Demanda acordada fora do kit padrão. Vira card de Briefing no Kanban.
                </p>
              </section>

              {contrato && (
                <section>
                  <SectionTitle>Contrato</SectionTitle>
                  <div className="grid grid-cols-2 gap-2">
                    <Info label="Código do cliente" value={contrato.identificador || cliente.id} />
                    <Info label="Status" value={contrato.status} />
                    <Info label="Cota" value={cota ? `${cota.familia} · ${cota.hierarquia}` : '—'} />
                    <Info label="Vigência" value={
                      `${contrato.vigenciaInicio ? new Date(contrato.vigenciaInicio).toLocaleDateString('pt-BR') : '—'} → ${contrato.vigenciaFim ? new Date(contrato.vigenciaFim).toLocaleDateString('pt-BR') : '—'}`
                    } />
                  </div>
                </section>
              )}

              {contrato && podeEncerrar && contrato.status !== 'Encerrado' && (
                <button
                  type="button"
                  onClick={() => {
                    if (!confirm(`Encerrar contrato de ${cliente.nome}? O cliente será movido para Inativos.`)) return;
                    encerrar.mutate(contrato.id);
                  }}
                  disabled={encerrar.isPending}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold text-red bg-red/10 border border-red/30 hover:bg-red/15 rounded-lg transition-colors disabled:opacity-50"
                >
                  {encerrar.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                  Encerrar contrato
                </button>
              )}

              {cota && (
                <section>
                  <SectionTitle>Escopo do kit</SectionTitle>
                  <div className="bg-panel-2 border border-border rounded-lg p-3 space-y-1.5">
                    <div className="font-semibold text-text">{cota.nome}</div>
                    <p className="text-text-2 leading-relaxed text-[11px]">
                      Kit padrão da família {cota.familia}. Itens detalhados ficam em <em>Itens de Kit de Cota</em> no Airtable.
                    </p>
                  </div>
                </section>
              )}
            </>
          )}

          {tab === 'escopo' && (
            <EscopoTab entregas={escopoEntregas} contratoId={contrato?.id} />
          )}

          {tab === 'entregas' && (
            <section>
              <SectionTitle>Timeline de entregas ({demandas.length})</SectionTitle>
              <p className="text-[11px] text-muted mb-3">
                Previstas pelo kit + adicionadas fora do escopo. Status atual e responsável de cada etapa.
              </p>
              {demandas.length === 0 ? (
                <div className="text-text-2 text-[11px] py-4 text-center">Nenhuma demanda atribuída a esse cliente ainda.</div>
              ) : (
                <div className="space-y-1.5">
                  {demandas
                    .slice()
                    .sort((a, b) => statusOrder(a.status) - statusOrder(b.status))
                    .map((d) => (
                      <DemandaRow key={d.id} d={d} pessoaById={pessoaById} areaById={areaById} />
                    ))}
                </div>
              )}
            </section>
          )}

          {tab === 'responsaveis' && (
            <>
              <ConviteSection cliente={cliente} />
              <section>
                <SectionTitle>Contatos do cliente</SectionTitle>
                {(() => {
                  const contatosCliente = pessoas.filter(
                    (p) => p.papel === 'Cliente' && (p.solicitanteIds ?? []).includes(cliente.id) && p.ativo,
                  );
                  if (!contatosCliente.length) {
                    return (
                      <p className="text-muted text-[10px] mt-1">
                        Nenhum contato cadastrado para este cliente. Use "Convidar" acima para gerar acesso.
                      </p>
                    );
                  }
                  return (
                    <div className="space-y-2">
                      {contatosCliente.map((p) => (
                        <div key={p.id} className="flex items-center gap-3 text-[11px] text-text-2">
                          <div className="w-7 h-7 rounded-full bg-blue/15 flex items-center justify-center text-blue-light text-[10px] font-bold shrink-0">
                            {p.nome.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-text truncate">{p.nome}</div>
                            <div className="flex gap-3 text-muted">
                              {p.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{p.email}</span>}
                              {p.telefone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{p.telefone}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </section>
            </>
          )}

          {tab === 'notas' && contrato && (
            <section>
              <SectionTitle>Notas do contrato</SectionTitle>
              <NotasPanel recursoTipo="contrato" recursoId={contrato.id} />
            </section>
          )}
          {tab === 'notas' && !contrato && (
            <p className="text-muted text-[11px]">Selecione um contrato para ver notas.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function statusOrder(s: StatusDemanda): number {
  return { 'Aguardando aprovação': 0, 'Em revisão': 1, 'Em produção': 2, 'Triagem': 3, 'Publicada': 4, 'Arquivada': 5, 'Cancelada': 6 }[s] ?? 99;
}

function DemandaRow({ d, pessoaById, areaById }: {
  d: Demanda;
  pessoaById: Map<string, Pessoa>;
  areaById: Map<string, Area>;
}) {
  const pill = STATUS_PILL[d.status];
  // Responsável: pega o primeiro de Aprovação, depois Revisão, depois Produção, depois Briefing — quem está agora
  const respIds =
    d.status === 'Aguardando aprovação' ? d.respAprovacaoIds :
    d.status === 'Em revisão' ? d.respRevisaoIds :
    d.status === 'Em produção' ? d.respProducaoIds :
    d.respBriefingIds;
  const respNomes = (respIds ?? []).map((id) => pessoaById.get(id)?.nome).filter(Boolean).join(', ');
  const areaNomes = d.areasIds.map((id) => areaById.get(id)?.nome).filter(Boolean).join(', ');
  const isPub = d.status === 'Publicada';

  return (
    <div className="bg-panel-2 border border-border rounded p-2.5">
      <div className="flex items-start gap-2 mb-1">
        <div className="mt-0.5">
          {isPub
            ? <CheckCircle2 className="w-3.5 h-3.5 text-green" />
            : <Clock className="w-3.5 h-3.5 text-orange" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11.5px] font-semibold text-text leading-snug">{d.titulo}</div>
          {areaNomes && <div className="text-[10px] text-text-2 truncate">{areaNomes}</div>}
        </div>
        <span className={clsx('shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide', pill.cls)}>
          {pill.label}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[10px] text-muted mt-1.5">
        <div>
          <span className="text-text-2">Responsável atual:</span>{' '}
          <span className="text-text">{respNomes || '—'}</span>
        </div>
        <div className="text-right">
          {isPub && d.dataPublicacao ? (
            <>Publicado em {new Date(d.dataPublicacao).toLocaleDateString('pt-BR')}</>
          ) : d.prazoSla ? (
            <>Prazo {new Date(d.prazoSla).toLocaleDateString('pt-BR')}</>
          ) : (
            <>Sem prazo</>
          )}
        </div>
      </div>
    </div>
  );
}



interface ConviteResult {
  usuario: string;
  senha: string;
  emailEnviado: boolean;
}

function ConviteSection({ cliente }: { cliente: Solicitante }) {
  const qc = useQueryClient();
  const pessoasQ = useQuery({ queryKey: ['pessoas'], queryFn: () => api.pessoas.list().then((r) => r.data) });
  const pessoas = pessoasQ.data ?? [];
  // Pessoas com papel Cliente vinculadas a esse solicitante
  const candidatos = useMemo(() => {
    return pessoas.filter((p) =>
      p.ativo
      && /cliente/i.test(p.papel as unknown as string)
      && (p.solicitanteIds ?? []).includes(cliente.id),
    );
  }, [pessoas, cliente.id]);

  const [resultado, setResultado] = useState<ConviteResult | null>(null);
  const convidar = useMutation({
    mutationFn: (pessoaId: string) => api.clientes.convidar(pessoaId),
    onSuccess: (data) => {
      setResultado({ usuario: data.usuario, senha: data.senha, emailEnviado: data.emailEnviado });
      qc.invalidateQueries({ queryKey: ['pessoas'] });
    },
  });

  function copiar(t: string) {
    try { navigator.clipboard.writeText(t); } catch { /* noop */ }
  }

  return (
    <section className="mb-4">
      <SectionTitle>Acesso ao portal</SectionTitle>
      {candidatos.length === 0 ? (
        <div className="bg-panel-2 border border-border rounded p-3 text-[11px] text-muted">
          Nenhuma pessoa com papel <strong>Cliente</strong> vinculada a esse solicitante.
          Cadastre primeiro na tabela Pessoas do Airtable (Papel = Cliente, Solicitantes = {cliente.nome}).
        </div>
      ) : (
        <div className="space-y-1.5">
          {candidatos.map((p) => {
            const jaTem = !!p.usuario;
            return (
              <div key={p.id} className="bg-panel-2 border border-border rounded p-2.5 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold text-text truncate">{p.nome}</div>
                  <div className="text-[10px] text-muted truncate">
                    {p.email ?? 'sem email'} {jaTem && <span className="text-green">· acesso ativo ({p.usuario})</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => convidar.mutate(p.id)}
                  disabled={convidar.isPending || !p.email}
                  className={clsx(
                    'inline-flex items-center gap-1 px-2 py-1.5 text-[11px] font-semibold rounded border transition-colors disabled:opacity-40',
                    jaTem
                      ? 'text-orange bg-orange/10 border-orange/30 hover:bg-orange/20'
                      : 'text-blue-light bg-blue/10 border-blue/30 hover:bg-blue/20',
                  )}
                  title={!p.email ? 'Pessoa sem email cadastrado' : jaTem ? 'Gerar nova senha' : 'Convidar pro portal'}
                >
                  {convidar.isPending
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <KeyRound className="w-3 h-3" />}
                  {jaTem ? 'Resetar senha' : 'Convidar'}
                </button>
              </div>
            );
          })}
        </div>
      )}
      {resultado && (
        <div className="mt-3 bg-green/10 border border-green/30 rounded p-3 space-y-2">
          <div className="text-[11px] font-bold text-green">
            ✓ Credenciais geradas {resultado.emailEnviado ? '· email enviado' : '· (email não pôde ser enviado, anote agora)'}
          </div>
          <CredRow label="Usuário" value={resultado.usuario} onCopy={() => copiar(resultado.usuario)} />
          <CredRow label="Senha" value={resultado.senha} onCopy={() => copiar(resultado.senha)} />
          <p className="text-[10px] text-text-2">
            A senha só aparece aqui agora — guarde se o email não chegou. O cliente pode entrar em <code className="text-blue-light">/login</code> aba <strong>Usuário e senha</strong>.
          </p>
        </div>
      )}
    </section>
  );
}

function CredRow({ label, value, onCopy }: { label: string; value: string; onCopy: () => void }) {
  return (
    <div className="flex items-center gap-2 bg-panel-2 border border-border rounded px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-muted w-14 shrink-0">{label}</div>
      <code className="flex-1 text-[12px] font-mono text-blue-light truncate">{value}</code>
      <button type="button" onClick={onCopy} className="text-muted hover:text-text" title="Copiar">
        <Copy className="w-3 h-3" />
      </button>
    </div>
  );
}

// ====== ESCOPO TAB ======
const STATUS_ENTREGA_COR: Record<StatusEntrega, string> = {
  'Pendente': 'bg-yellow/15 text-yellow border-yellow/30',
  'Em produção': 'bg-blue/15 text-blue-light border-blue/30',
  'Aguardando aprovação': 'bg-purple/15 text-purple border-purple/30',
  'Entregue': 'bg-green/15 text-green border-green/30',
  'Revisão solicitada': 'bg-red/15 text-red border-red/30',
};

function EscopoTab({ entregas, contratoId }: { entregas: EntregaEscopo[]; contratoId?: string }) {
  const qc = useQueryClient();
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<EntregaEscopo> }) => api.escopoEntregas.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['escopo-entregas', contratoId] }),
  });

  if (entregas.length === 0) {
    return (
      <div className="text-center py-8 text-text-2 text-[11px]">
        Nenhum escopo de entregas definido para este contrato.<br />
        <span className="text-muted">O escopo é criado ao cadastrar o contrato.</span>
      </div>
    );
  }

  // Agrupar: evento → fase → entregas
  const porEvento = new Map<string, EntregaEscopo[]>();
  for (const e of entregas) {
    const key = e.eventoSubtipo || 'Geral';
    if (!porEvento.has(key)) porEvento.set(key, []);
    porEvento.get(key)!.push(e);
  }

  return (
    <div className="space-y-4">
      {Array.from(porEvento.entries()).map(([evento, items]) => {
        const porFase = new Map<FaseEscopo, EntregaEscopo[]>();
        for (const e of items) {
          if (!porFase.has(e.fase)) porFase.set(e.fase, []);
          porFase.get(e.fase)!.push(e);
        }
        const total = items.length;
        const entregues = items.filter((e) => e.status === 'Entregue').length;

        return (
          <div key={evento} className="bg-panel-2/40 border border-border rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-bold text-text uppercase tracking-wider">{evento}</div>
              <div className="text-[10px] text-muted">
                <span className="text-green font-bold">{entregues}</span>/{total} entregues
              </div>
            </div>

            {/* Barra de progresso */}
            <div className="h-1.5 bg-panel-2 rounded-full mb-3 overflow-hidden">
              <div
                className="h-full bg-green rounded-full transition-all duration-300"
                style={{ width: `${total > 0 ? (entregues / total) * 100 : 0}%` }}
              />
            </div>

            {FASES_ESCOPO.map((fase) => {
              const faseItems = porFase.get(fase);
              if (!faseItems?.length) return null;
              return (
                <div key={fase} className="mb-2">
                  <div className="text-[10px] font-semibold text-text-2 uppercase tracking-wider mb-1">{fase}</div>
                  <div className="space-y-1 ml-2">
                    {faseItems.map((e) => (
                      <div key={e.id} className="flex items-center gap-2 py-1">
                        <select
                          value={e.status}
                          onChange={(ev) => updateMut.mutate({ id: e.id, body: { status: ev.target.value as StatusEntrega } })}
                          className={clsx(
                            'text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border cursor-pointer',
                            STATUS_ENTREGA_COR[e.status],
                          )}
                        >
                          <option value="Pendente">Pendente</option>
                          <option value="Em produção">Em produção</option>
                          <option value="Aguardando aprovação">Aguardando aprovação</option>
                          <option value="Entregue">Entregue</option>
                          <option value="Revisão solicitada">Revisão solicitada</option>
                        </select>
                        <span className="text-[11px] text-text flex-1">{e.entrega}</span>
                        {e.quantidade > 1 && (
                          <span className="text-[10px] text-muted">×{e.quantidade}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="text-[10px] font-semibold text-text-2 uppercase tracking-wide mb-1.5">{children}</h3>;
}

function Info({ label, value }: { label: string; value: string }) {
  const isCode = /código/i.test(label);
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-muted mb-0.5 flex items-center gap-1">
        {isCode && <Hash className="w-2.5 h-2.5" />}
        {label}
      </div>
      <div className={clsx('font-semibold text-xs', isCode ? 'text-blue-light font-mono' : 'text-text')}>{value}</div>
    </div>
  );
}

function MiniKpi({ label, value, color }: { label: string; value: number; color: 'blue' | 'orange' | 'pink' | 'green' }) {
  const map = {
    blue: 'border-blue/40 text-blue-light',
    orange: 'border-orange/40 text-orange',
    pink: 'border-pink/40 text-pink',
    green: 'border-green/40 text-green',
  };
  return (
    <div className={clsx('bg-panel-2 border border-border border-2 rounded-md p-2 text-center', map[color])}>
      <div className="text-lg font-black">{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-muted">{label}</div>
    </div>
  );
}
